/**
 * Upload limits shared by the dropzones and the upload pipeline (C11).
 *
 * The numbers mirror the backend constants in `censuszen/definitions.py`
 * (MAX_UPLOAD_BYTES, MAX_FILES_PER_CASE). The dropzone refuses a bad file
 * before any request; the server repeats the checks and answers a 400
 * with one of the codes below when a request slips past.
 */

export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
export const MAX_FILES_PER_CASE = 30;

export const ACCEPT = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

export const FILE_TOO_LARGE_MESSAGE = 'Files must be 100 MB or smaller';
export const FILE_INVALID_TYPE_MESSAGE = 'Only PDF and Word (.docx) files are accepted';
export const TOO_MANY_FILES_MESSAGE = 'A case can hold up to 30 files';
export const CASE_FULL_MESSAGE = 'This case holds the maximum of 30 files';
const UNKNOWN_REJECTION_MESSAGE = 'This file cannot be uploaded';

// react-dropzone error codes -> the fixed toast text.
const REJECTION_MESSAGES = {
    'file-too-large': FILE_TOO_LARGE_MESSAGE,
    'file-invalid-type': FILE_INVALID_TYPE_MESSAGE,
    'too-many-files': TOO_MANY_FILES_MESSAGE,
};

// Backend 400 codes whose `error` text is safe and specific enough to show as is.
const UPLOAD_ERROR_CODES = new Set([
    'unsupported_file_type',
    'file_too_large',
    'too_many_files',
    'too_many_pages',
    'upload_not_found',
    'unreadable_pdf',
]);

// How many filenames a toast spells out before it falls back to a count.
const NAMES_IN_TOAST = 5;

/**
 * Distinct toast messages for a react-dropzone `fileRejections` list, in
 * first-seen order. Ten oversized files produce one toast, not ten.
 *
 * Each message names the files it refers to. react-dropzone 20 accepts the
 * in-limit prefix of an over-limit drop and rejects only the surplus, so
 * "A case can hold up to 30 files" on its own reads as though nothing was
 * added when in fact 30 were. The count and the names say which documents
 * the coordinator still has to deal with.
 *
 * @param {Array<{file: File, errors: Array<{code: string}>}>} [fileRejections]
 * @returns {string[]}
 */
export function rejectionMessages(fileRejections) {
    // reason -> filenames, insertion-ordered by Map.
    const byReason = new Map();
    (fileRejections || []).forEach((rejection) => {
        (rejection?.errors || []).forEach((error) => {
            const reason = REJECTION_MESSAGES[error?.code] || UNKNOWN_REJECTION_MESSAGE;
            if (!byReason.has(reason)) byReason.set(reason, []);
            byReason.get(reason).push(rejection?.file?.name);
        });
    });

    return Array.from(byReason, ([reason, names]) => {
        const known = names.filter(Boolean);
        const shown = known.slice(0, NAMES_IN_TOAST).join(', ');
        const rest = known.length - NAMES_IN_TOAST;
        const listed = [shown, rest > 0 ? `and ${rest} more` : ''].filter(Boolean).join(' ');
        return `${reason}. ${names.length} not added${listed ? `: ${listed}` : ''}`;
    });
}

/**
 * The server's fixed `error` text when an upload request failed on one of
 * the C11 limits (400 with a known code); `null` for every other error so
 * the caller keeps its generic handling.
 *
 * @param {any} error - an axios error (or anything else)
 * @returns {string|null}
 */
export function uploadErrorMessage(error) {
    const response = error?.response;
    if (!response || response.status !== 400) return null;
    const data = response.data || {};
    if (!UPLOAD_ERROR_CODES.has(data.code)) return null;
    return typeof data.error === 'string' && data.error ? data.error : null;
}
